import { reduce } from 'lodash-es'
import type { ServerLDFlagSet } from 'types/launchdarkly-node-server-sdk'
import type { ClientLDFlagSet } from 'types/launchdarkly-js-client-sdk'

/**
 * @description This file defines which LaunchDarkly flags the client should listen to.
 *
 * Flag Management:
 * 1. Adding a new flag:
 *    - Create the flag in LaunchDarkly dashboard
 *    - Add the flag key to the `flags` array below (in its original form as known to LaunchDarkly)
 *    - Run `pnpm ld-codegen` to regenerate types
 *
 * 2. Removing a flag:
 *    - Remove the flag key from the `flags` array below
 *    - Run `pnpm ld-codegen` to regenerate types
 *    - Run `pnpm ts` to verify there is no remaining references to the flag in the codebase
 *
 * Note: Flag keys must be in their original form as known to LaunchDarkly (not camelCased).
 * Run `pnpm ld-codegen:verbose` to see detailed information about flag usage and potential issues.
 */
export const flags = reduce<keyof ServerLDFlagSet, ServerLDFlagSet>(
  [
    'cin-7-visibility',
    'disable-channel-forecast-break-down',
    'disable-xero-2-way-sync',
    'hide-2-way-xero-sync',
    'hide-xero',
    'ignore-landed-costs-upsell',
    'lockbox-reporting',
    'mui-theme',
    'payments-page-backfill-epoch',
    'sync-receivable-invoices-qbo',
    'use-shopify-app-store-connect-link',
    'ai-command-bar',
    'tally-page',
    'firm-clients-saa-s-autocharge',
    'saas-sales-tax',
    'qbo-catalog-and-po-sync',
    'temp-hide-financial-exports', // https://linear.app/settle/issue/STL-3331/hide-some-financials-related-ui-behind-a-ff
    'working-capital-lending-license', // https://linear.app/settle/issue/STL-4224/fe-update-caption-mentioning-sloc-settle-lending-of-california-on
    'payment-scheduled-confirmation-download', // https://linear.app/settle/issue/STL-4915
    'default-interest-customer-view', // https://linear.app/settle/issue/STL-4918
    'financed-consolidated-payment', // https://linear.app/settle/issue/STL-5060
    'scheduled-reports', // https://linear.app/settle/issue/STL-5016
    'list-items-on-po-matrix-view', // https://linear.app/settle/issue/STL-3994
    'timezone-aware-timestamps', // https://linear.app/settle/issue/STL-3665
  ],
  (flags, flag) => ({ ...flags, [flag]: undefined }),
  {},
) as ClientLDFlagSet
